import {useCallback, useEffect, useState} from "react";
import {useUserInfoGetter} from "./hooks/useUserInfoGetter";
import {OrderDetail} from "../../types/order";
import {useCookie} from "../../hooks/auth/useCookie";
import {CookieKeys} from "../../hooks/auth/AuthProvider";
import {useNavigate, useParams} from "react-router-dom";
import {useAppDispatch, useAuth} from "../../hooks/hooks";
import {getOrderDetailAsync} from "../../store/order/orderSlice";
import {ErrorType} from "../../types/error";
declare global {
    interface Window {
        wcs: any;
        wcs_add?: any;
    }
}

export function GoogleAnalytics() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {getUserInfo, user} = useUserInfoGetter();
    const {getValueFromCookie} = useCookie();
    const accessToken = getValueFromCookie(CookieKeys.ACCESS_TOKEN);
    const [eventFired, setEventFired] = useState(false);
    const [orderCompleted, setOrderCompleted] = useState(false);

    const {orderIdx} = useParams<"orderIdx">();
    const [orderDetail, setOrderDetail] = useState<OrderDetail | null>(null);

    const {isLoggedIn} = useAuth();

    useEffect(() => {
        if (!isLoggedIn) return;
        if (!orderIdx) return;
        getOrder().then();
        // eslint-disable-next-line
    }, []);


    // 주문완료일 경우 주문정보 조회
    const getOrder = useCallback(async () => {
        try {
            const orderDetail = await dispatch(getOrderDetailAsync(Number(orderIdx))).unwrap();
            setOrderDetail(orderDetail);
        } catch (error) {
            const errorObj: ErrorType = JSON.parse((error as Error).message);
            console.log('error : ', errorObj)
        }
    }, [dispatch, orderIdx]);

    useEffect(() => {
        if (accessToken) {
            getUserInfo().then();
        }
    }, [accessToken, getUserInfo]);


    // ga4 이벤트 수집
    useEffect(() => {
        initializeGA4();
        initializeNA();

        // 주문완료 이벤트
        if (orderDetail && !orderCompleted) {
            setOrderCompleted(true);

            // 구글
            window.gtag('event', 'order_completed', {
                'idx': orderDetail.idx,
                'pay_amount': orderDetail.payAmount,
                'order_quantity': orderDetail.orderQuantity,
                'discount_amount': orderDetail.productDiscountAmount,
            });
            console.log('주문완료 이벤트 추가 : ', orderDetail);

            // 네이버
            if (!window.wcs_add) window.wcs_add = {};
            window.wcs_add["wa"] = "s_282cfc2b7159";

            const _conv = {
                type: 'purchase',
                value: String(orderDetail.payAmount),
                id: String(orderDetail.idx),
                items: [{
                    id: String(orderDetail.idx),
                    name: orderDetail.orderTitle,
                    payAmount: String(orderDetail.payAmount)
                }]
            };
            console.log('_conv : ',_conv)
            window.wcs.trans(_conv);

        }
        // eslint-disable-next-line
    }, [navigate, orderDetail
        // accessToken, user, eventFired
    ]);


    // ga4 초기화
    const initializeGA4 = () => {
        const userId = user ? user.idx : 'unsigned_user';

        // GA4 라이브러리 추가
        const script1 = document.createElement('script');
        script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-WJ2BNFM66N';
        script1.async = true;
        window.gtag('js', new Date());

        const handleScriptLoad = () => {
            // 사용자 로그인 상태 체크 이벤트
            window.gtag('config', 'G-WJ2BNFM66N', {
                'user_id': userId,
            });

            // 현재 페이지 제목 체크 이벤트
            window.gtag('event', 'page_view', {
                'page_title': checkCurrentPageInfo(),
                'page_path': window.location.pathname
            });
            // console.log('GA4 userId 추가 : ', userId);
            // console.log('GA4 페이지 경로 추가 : ', checkCurrentPageInfo());
        };


        // 최초 접근 시 이벤트 한번만 발생하도록 조건 처리
        if (!eventFired && ((accessToken && user !== undefined) || (!accessToken && !user))) {
            setEventFired(true);
            script1.onload = handleScriptLoad;
            document.head.appendChild(script1);

            return () => {
                document.head.removeChild(script1);
            };
        }

        // 최초 접근 이후 페이지 이동 이벤트 감지
        if (eventFired) {
            // setIsFirstLoaded(false);
            script1.onload = handleScriptLoad;
            document.head.appendChild(script1);

            return () => {
                document.head.removeChild(script1);
            };
        }
    };

    // 네이버 로그 초기화
    const initializeNA = () => {
        const userId = user ? user.idx : 'unsigned_user';

        // 네이버 로그 분석 라이브러리 추가
        const naverScript = document.createElement('script');
        naverScript.src = '//wcs.naver.net/wcslog.js';
        naverScript.async = true;

        const handleScriptLoad = () => {
            // 네이버 추적 코드 초기화
            const naverTrackingScript = document.createElement('script');
            naverTrackingScript.innerHTML = `
            if (!wcs_add) var wcs_add={};
            wcs_add["wa"] = "s_282cfc2b7159";
            if (!_nasa) var _nasa={};
            
            // 사용자 정보 추가
            _nasa["uid"] = "${userId}";
            
            // 페이지 정보 추가
            _nasa["pa"] = "${checkCurrentPageInfo()}";
            _nasa["u"] = "${window.location.pathname}";
            
            if(window.wcs){
                wcs.inflow();
                wcs_do(_nasa);
            }
        `;

            document.head.appendChild(naverTrackingScript);
            // console.log('naver userId 추가 : ', userId);
            // console.log('naver 페이지 경로 추가 : ', checkCurrentPageInfo());
        };

        // 최초 접근 시 이벤트 한번만 발생하도록 조건 처리
        if (!eventFired && ((accessToken && user !== undefined) || (!accessToken && !user))) {
            setEventFired(true);
            naverScript.onload = handleScriptLoad;
            document.head.appendChild(naverScript);

            return () => {
                document.head.removeChild(naverScript);
            };
        }

        // 최초 접근 이후 페이지 이동 이벤트 감지
        if (eventFired) {
            naverScript.onload = handleScriptLoad;
            document.head.appendChild(naverScript);

            return () => {
                document.head.removeChild(naverScript);
            };
        }
    };

    // 사용자 홈페이지 이용 경로 트래킹
    const checkCurrentPageInfo = () => {
        const pathTitleMap = [
            {path: 'review', title: "리얼 후기 페이지"},
            {path: 'purchase-voucher', title: "이용권 구매 페이지"},
            {path: 'order', title: "주문 페이지"},
            {path: 'mypage', title: "마이 페이지"},
            {path: 'payment-result', title: "주문 완료 페이지"},
            {path: 'understanding', title: "검사의 이해"},
            {path: 'purpose-and-characteristics', title: "검사의 개발 목적과 특징"},
            {path: 'researcher', title: "문수백 연구소장님"},
            {path: 'download-test', title: "검사 다운로드 페이지"},
            {path: 'partnership', title: "제휴 문의 페이지"},
            {path: 'partnerlist', title: "협력사"}
        ];

        const currentPath = window.location.pathname;
        const matchedPath = pathTitleMap.find(mapping => currentPath.includes(mapping.path));

        return matchedPath ? matchedPath.title : "뇌 기반 재능 발굴의 시작 케이패스";
    };

    return (
        <>
        </>
    )
}